import { Button } from "@/components/ui/button";
import { ArrowDown } from "lucide-react";
import TypewriterText from "@/components/TypewriterText";
import StatusHeader from "@/components/StatusHeader";
import { useLanguage } from "@/context/LanguageContext";

const TYPEWRITER_WORDS = [
  "Organizations",
  "Start-Ups",
  "People",
  "Teams",
  "Leaderships",
  "Competences",
  "Strategies",
  "Systems",
  "Behavior",
  "Viability"
];

const Hero = () => {
  const { language, setLanguage, t } = useLanguage();

  const scrollToCategories = () => {
    document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <StatusHeader />
      {/* Language Toggle moved to global Menu */}
      {/* Background canvas will be visible here */}
      {/* Text overlay with slight transparency to ensure readability */}
      <div className="absolute inset-0 bg-background/10 backdrop-blur-[1px] z-0 pointer-events-none" />
      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-glow-pulse z-0" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/15 rounded-full blur-3xl animate-glow-pulse z-0" style={{ animationDelay: '1.5s' }} />

      <div className="container mx-auto px-2 sm:px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
          <div className="space-y-6 md:space-y-8">
            {/* SEO: Static H1 for search engines targeting "Organizational Development Heilbronn" */}
            <h1 className="sr-only">{t("seo.home.h1")}</h1>

            {/* Name and Title Block - At the very top */}
            <div className="mb-4 md:mb-6 text-left w-full max-w-4xl mx-auto px-2 sm:px-4">
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-bold text-foreground leading-tight">
                  {t("hero.name")}
                </span>
                <span className="text-xs md:text-sm text-muted-foreground font-medium">
                  {t("hero.title")}
                </span>
              </div>
            </div>

            {/* Typewriter Animation */}
            <div className="h-28 md:h-32 flex items-start justify-center pb-2 md:pb-0">
              {/* Visual "H1" is actually a div to avoid duplicate H1s */}
              {/* SEO: Changed to h2 to improve document outline (H1 -> H2 hierarchy) while keeping visual style */}
              <h2 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight" aria-hidden="true">
                <TypewriterText
                  staticText="Developing "
                  words={TYPEWRITER_WORDS}
                  typingSpeed={120}
                  deletingSpeed={80}
                  pauseDuration={2000}
                />
              </h2>
            </div>

            {/* New Description Text */}
            <div className="max-w-3xl mx-auto space-y-4 text-lg md:text-xl text-muted-foreground leading-relaxed">
              <p>
                {t("hero.description1")}
              </p>

            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <Button
              variant="hero"
              size="lg"
              onClick={scrollToCategories}
              className="group"
            >
              {t("hero.cta.primary")}

            </Button>
            <Button
              variant="glass"
              size="lg"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t("hero.cta.secondary")}
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToCategories}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce p-2 rounded-full hover:bg-primary/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t("hero.scrollDown")}
      >
        <ArrowDown className="h-6 w-6 text-muted-foreground" />
      </button>
    </section>
  );
};

export default Hero;
